import {Image} from 'antd';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useDocumentOverlayDimensions} from '../hooks/useDocumentOverlayDimensions';
import SVGBoundingBox from '../SVGBoundingBox';
import {NormalizedPolygon} from '../utils/polygonUtils';

const WRAPPER_STYLE: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '100%',
  maxHeight: '100%'
};

const FILL_CONTAINER_WRAPPER_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const FILL_CONTAINER_IMAGE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain'
};

const PREVIEW_WRAPPER_STYLE: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block'
};

interface OverlayLayerProps {
  polygons: NormalizedPolygon[];
  page: number;
  imageElement: HTMLImageElement | null;
}

const OverlayLayer: React.FC<OverlayLayerProps> = ({
  polygons,
  page,
  imageElement
}) => {
  const {width, height, offsetX, offsetY, isReady} =
    useDocumentOverlayDimensions(imageElement);

  if (!isReady || !polygons.length || !width || !height) {
    return null;
  }

  return (
    <SVGBoundingBox
      polygons={polygons}
      imageDimension={{width, height}}
      page={page}
      offsetX={offsetX}
      offsetY={offsetY}
    />
  );
};

interface PreviewImageWithOverlayProps {
  originalNode: React.ReactElement;
  polygons: NormalizedPolygon[];
  page: number;
}

const PreviewImageWithOverlay: React.FC<PreviewImageWithOverlayProps> = ({
  originalNode,
  polygons,
  page
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null
  );

  const syncImageElement = useCallback(() => {
    const img = wrapperRef.current?.querySelector('img');
    if (img) {
      setImageElement(img);
    }
  }, []);

  useEffect(() => {
    syncImageElement();
  }, [syncImageElement, originalNode]);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      setImageElement(event.currentTarget);
      const originalOnLoad = (
        originalNode.props as {onLoad?: (e: React.SyntheticEvent) => void}
      ).onLoad;
      originalOnLoad?.(event);
    },
    [originalNode]
  );

  const mergedNode = React.cloneElement(originalNode, {
    onLoad: handleLoad
  });

  return (
    <div ref={wrapperRef} style={PREVIEW_WRAPPER_STYLE}>
      {mergedNode}
      <OverlayLayer
        polygons={polygons}
        page={page}
        imageElement={imageElement}
      />
    </div>
  );
};

interface ImageWithPolygonOverlayProps {
  src: string;
  alt?: string;
  style?: React.CSSProperties;
  polygons?: NormalizedPolygon[];
  page?: number;
  fillContainer?: boolean;
  onLoad?: (target: EventTarget | null) => void;
}

const ImageWithPolygonOverlay: React.FC<ImageWithPolygonOverlayProps> = ({
  src,
  alt,
  style,
  polygons = [],
  page = 1,
  fillContainer = false,
  onLoad
}) => {
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
    null
  );

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const target = event.target as HTMLImageElement;
      setImageElement(target);
      onLoad?.(target);
    },
    [onLoad]
  );

  const previewConfig = useMemo(() => {
    if (!polygons.length) {
      return true;
    }

    return {
      imageRender: (originalNode: React.ReactElement) => (
        <PreviewImageWithOverlay
          originalNode={originalNode}
          polygons={polygons}
          page={page}
        />
      )
    };
  }, [polygons, page]);

  const wrapperStyle = fillContainer
    ? FILL_CONTAINER_WRAPPER_STYLE
    : WRAPPER_STYLE;

  const imageStyle = fillContainer
    ? {...FILL_CONTAINER_IMAGE_STYLE, ...style}
    : style;

  return (
    <div style={wrapperStyle}>
      <Image
        src={src}
        alt={alt}
        style={imageStyle}
        preview={previewConfig}
        onLoad={handleLoad}
      />
      <OverlayLayer
        polygons={polygons}
        page={page}
        imageElement={imageElement}
      />
    </div>
  );
};

export default React.memo(ImageWithPolygonOverlay);
